import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from '../components/Navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft, Milestone, Plus, Check, X, Clock, ShieldCheck,
  IndianRupee, FileText, Download, CheckCircle, AlertCircle, XCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  requested: { label: 'Awaiting Client', color: 'bg-amber-100 text-amber-700', icon: Clock },
  client_approved: { label: 'Awaiting Admin', color: 'bg-blue-100 text-blue-700', icon: ShieldCheck },
  admin_approved: { label: 'Admin Approved', color: 'bg-green-100 text-green-700', icon: Check },
  released: { label: 'Paid', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const MilestoneSteps = ({ status }) => {
  const steps = [
    { label: 'Submitted', done: true },
    { label: 'Client OK', done: ['client_approved', 'released'].includes(status) },
    { label: 'Released', done: status === 'released' },
  ];
  if (status === 'rejected') return null;
  return (
    <div className="flex items-center gap-0 mt-3">
      {steps.map((step, idx) => (
        <React.Fragment key={idx}>
          <div className="flex flex-col items-center">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${step.done ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
              {step.done ? <Check className="w-3 h-3" /> : idx + 1}
            </div>
            <span className={`text-xs mt-1 ${step.done ? 'text-green-600 font-medium' : 'text-slate-400'}`}>{step.label}</span>
          </div>
          {idx < steps.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 mb-4 ${steps[idx + 1].done ? 'bg-green-400' : 'bg-slate-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const ContractDetail = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contract, setContract] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
  const [newMilestone, setNewMilestone] = useState({ title: '', description: '', amount: '' });
  const [submitting, setSubmitting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(null);
  const [resubmitDialog, setResubmitDialog] = useState(null);
  const [resubmitData, setResubmitData] = useState({ title: '', description: '', amount: '' });
  const [feePct, setFeePct] = useState(2);

  useEffect(() => {
    fetchContract();
    axios.get(`${API}/settings/platform-fee`)
      .then(res => setFeePct(res.data.platform_fee_percentage ?? 2))
      .catch(() => {});
  }, [contractId]);

  const fetchContract = async () => {
    try {
      const [contractRes, milestonesRes] = await Promise.all([
        axios.get(`${API}/contracts/${contractId}`),
        axios.get(`${API}/contracts/${contractId}/milestones`).catch(() => ({ data: [] }))
      ]);
      setContract(contractRes.data);
      setMilestones(milestonesRes.data);
    } catch (error) {
      toast.error('Contract not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleStripePayment = async () => {
    try {
      setProcessingPayment(true);
      const res = await axios.post(`${API}/payments/checkout`, { contract_id: contractId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (error) {
      toast.error('Failed to initiate payment');
      setProcessingPayment(false);
    }
  };

  const handleRazorpayPayment = async () => {
    try {
      setProcessingPayment(true);
      const orderRes = await axios.post(`${API}/payments/razorpay/create-order`, { contract_id: contractId, payment_method: 'razorpay' });
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      document.body.appendChild(script);
      script.onload = () => {
        const razorpay = new window.Razorpay({
          key: orderRes.data.key_id, amount: orderRes.data.amount, currency: orderRes.data.currency,
          name: 'TaskPiece', description: `Payment for Contract #${contract.id.slice(0, 8)}`,
          order_id: orderRes.data.order_id,
          handler: async (response) => {
            try {
              await axios.post(`${API}/payments/razorpay/verify`, null, { params: { razorpay_order_id: response.razorpay_order_id, razorpay_payment_id: response.razorpay_payment_id, razorpay_signature: response.razorpay_signature } });
              toast.success('Payment successful!'); fetchContract();
            } catch { toast.error('Verification failed'); } finally { setProcessingPayment(false); }
          },
          prefill: { name: user?.full_name || '', email: user?.email || '' },
          theme: { color: '#1a5cdb' }
        });
        razorpay.on('payment.failed', () => { toast.error('Payment failed'); setProcessingPayment(false); });
        razorpay.open();
      };
    } catch { toast.error('Failed to initiate payment'); setProcessingPayment(false); }
  };

  const handlePayment = () => paymentMethod === 'stripe' ? handleStripePayment() : handleRazorpayPayment();

  const handleReleasePayment = async () => {
    try { await axios.post(`${API}/payments/release/${contractId}`); toast.success('Payment released!'); fetchContract(); } catch { toast.error('Failed'); }
  };

  const handleCreateMilestone = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contracts/${contractId}/milestones`, {
        ...newMilestone,
        amount: parseFloat(newMilestone.amount)
      });
      toast.success('Milestone added to contract plan!');
      setShowMilestoneDialog(false);
      setNewMilestone({ title: '', description: '', amount: '' });
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create milestone');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.patch(`${API}/milestones/${resubmitDialog}/resubmit`, {
        ...resubmitData,
        amount: parseFloat(resubmitData.amount)
      });
      toast.success('Milestone resubmitted for approval!');
      setResubmitDialog(null);
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubmit');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClientApprove = async (milestoneId) => {
    try {
      await axios.patch(`${API}/milestones/${milestoneId}/client-approve`);
      toast.success('Milestone approved! Awaiting admin approval.');
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleAdminApprove = async (milestoneId) => {
    try {
      const res = await axios.patch(`${API}/milestones/${milestoneId}/admin-approve`);
      toast.success('Payment released to freelancer wallet!');
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleReject = async (milestoneId) => {
    try {
      await axios.patch(`${API}/milestones/${milestoneId}/reject?reason=${encodeURIComponent(rejectReason || 'Not approved')}`);
      toast.success('Milestone rejected');
      setShowRejectDialog(null);
      setRejectReason('');
      fetchContract();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const handleDownloadInvoice = () => {
    window.open(`${API}/invoices/${contractId}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navigation />
        <div className="flex items-center justify-center h-96">Loading...</div>
      </div>
    );
  }

  if (!contract) {
    return null;
  }

  const isClient = user?.id === contract.client_id;
  const isFreelancer = user?.id === contract.freelancer_id;
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const totalPaid = milestones.filter(m => m.status === 'released').reduce((s, m) => s + m.amount, 0);
  const pendingAmount = milestones.filter(m => ['requested', 'client_approved'].includes(m.status)).reduce((s, m) => s + m.amount, 0);
  const remaining = contract.amount - totalPaid - pendingAmount;
  const progressPercent = contract.amount > 0 ? (totalPaid / contract.amount) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <Button variant="ghost" onClick={() => navigate('/dashboard')} className="mb-6" data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Button>

        {/* Contract Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-2xl font-heading" data-testid="contract-title">
                  Contract #{contract.id.slice(0, 8)}
                </CardTitle>
                <CardDescription>
                  {contract.client_name} &rarr; {contract.freelancer_name}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Badge variant={contract.status === 'active' ? 'default' : 'secondary'} data-testid="contract-status">{contract.status}</Badge>
                <Badge variant="outline" data-testid="payment-status">{contract.payment_status}</Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div>
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-2xl font-heading font-bold text-secondary" data-testid="contract-amount">₹{contract.amount.toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Paid via Milestones</p>
                <p className="text-2xl font-heading font-bold text-green-600">₹{totalPaid.toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-heading font-bold text-amber-600">₹{pendingAmount.toLocaleString('en-IN')}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Delivery</p>
                <p className="text-2xl font-heading font-bold">{contract.delivery_days}d</p>
              </div>
            </div>

            {/* Progress Bar */}
            {milestones.length > 0 && (
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Payment Progress</span>
                  <span className="font-medium">{progressPercent.toFixed(0)}%</span>
                </div>
                <Progress value={progressPercent} className="h-2" data-testid="payment-progress" />
              </div>
            )}

            {/* Actions Row */}
            <div className="flex flex-wrap gap-2">
              {(contract.payment_status === 'escrowed' || contract.payment_status === 'released') && (
                <Button variant="outline" size="sm" onClick={handleDownloadInvoice} data-testid="download-invoice">
                  <Download className="w-4 h-4 mr-1" /> GST Invoice
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Escrow Payment Section (for non-milestone full payment) */}
        {isClient && contract.payment_status === 'pending' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Fund Escrow</CardTitle>
              <CardDescription>Deposit funds to activate the contract</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="w-full md:w-64" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="razorpay">Razorpay (UPI, Cards, Net Banking)</SelectItem>
                    <SelectItem value="stripe">Stripe (International Cards)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handlePayment} disabled={processingPayment} data-testid="pay-button">
                {processingPayment ? 'Processing...' : `Pay ₹${contract.amount.toLocaleString('en-IN')}`}
              </Button>
            </CardContent>
          </Card>
        )}

        {isClient && contract.payment_status === 'escrowed' && contract.status === 'active' && !contract.milestones_enabled && (
          <Card className="mb-6" data-testid="release-payment-card">
            <CardHeader>
              <CardTitle className="text-lg">Release Payment</CardTitle>
              <CardDescription>Release the escrowed funds once the work is complete.</CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const gross = contract.amount;
                const fee = Math.round((gross * feePct / 100) * 100) / 100;
                const net = Math.round((gross - fee) * 100) / 100;
                return (
                  <div className="rounded-lg border bg-slate-50 p-4 mb-4 text-sm" data-testid="fee-breakdown">
                    <div className="flex justify-between py-1">
                      <span className="text-muted-foreground">Contract amount (in escrow)</span>
                      <span className="font-medium" data-testid="breakdown-gross">₹{gross.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="flex justify-between py-1 text-amber-700">
                      <span>Platform fee ({feePct}%)</span>
                      <span className="font-medium" data-testid="breakdown-fee">− ₹{fee.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="border-t mt-2 pt-2 flex justify-between">
                      <span className="font-semibold text-primary">Freelancer receives (net)</span>
                      <span className="font-heading font-bold text-green-600" data-testid="breakdown-net">₹{net.toLocaleString('en-IN')}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">A {feePct}% platform fee is deducted from the payout at the time of release and the net amount is credited to the freelancer's wallet.</p>
                  </div>
                );
              })()}
              <Button onClick={handleReleasePayment} data-testid="release-payment-button">Release ₹{contract.amount.toLocaleString('en-IN')} to Freelancer</Button>
            </CardContent>
          </Card>
        )}

        {/* Milestones Section */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Milestone className="w-5 h-5" /> Milestones
                </CardTitle>
                <CardDescription>
                  {contract.payment_status === 'pending'
                    ? 'Plan your milestones before the client funds the escrow'
                    : 'Track milestone approvals and payment releases'}
                </CardDescription>
              </div>
              {isFreelancer && contract.payment_status === 'pending' && remaining > 0 && (
                <Dialog open={showMilestoneDialog} onOpenChange={setShowMilestoneDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="request-milestone-button">
                      <Plus className="w-4 h-4 mr-2" /> Add Milestone
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Add Milestone</DialogTitle>
                      <DialogDescription>
                        Plan a milestone for this contract. Available: ₹{remaining.toLocaleString('en-IN')}
                      </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateMilestone} className="space-y-4" data-testid="milestone-form">
                      <div>
                        <Label>Milestone Title</Label>
                        <Input
                          value={newMilestone.title}
                          onChange={e => setNewMilestone({...newMilestone, title: e.target.value})}
                          required placeholder="e.g. Phase 1 - UI Design"
                          data-testid="milestone-title"
                        />
                      </div>
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={newMilestone.description}
                          onChange={e => setNewMilestone({...newMilestone, description: e.target.value})}
                          required rows={3} placeholder="Describe the deliverables for this milestone..."
                          data-testid="milestone-description"
                        />
                      </div>
                      <div>
                        <Label>Amount (₹) — Max: ₹{remaining.toLocaleString('en-IN')}</Label>
                        <Input
                          type="number" min="1" max={remaining}
                          value={newMilestone.amount}
                          onChange={e => setNewMilestone({...newMilestone, amount: e.target.value})}
                          required placeholder="15000"
                          data-testid="milestone-amount"
                        />
                      </div>
                      <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-milestone">
                        {submitting ? 'Adding...' : 'Add Milestone'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {milestones.length === 0 ? (
              <div className="text-center py-8">
                <Milestone className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No milestones planned yet</p>
                {isFreelancer && contract.payment_status === 'pending' && (
                  <p className="text-sm text-muted-foreground mt-1">Plan your milestones before the client funds the contract</p>
                )}
                {isFreelancer && contract.payment_status !== 'pending' && (
                  <p className="text-sm text-amber-600 mt-1">Milestones must be planned before the contract is funded</p>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {milestones.map((ms, idx) => {
                  const config = STATUS_CONFIG[ms.status] || STATUS_CONFIG.requested;
                  const StatusIcon = config.icon;
                  return (
                    <div key={ms.id} className={`border rounded-lg p-4 ${ms.status === 'released' ? 'bg-green-50 border-green-200' : ms.status === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-white'}`} data-testid={`milestone-${ms.id}`}>
                      <div className="flex items-start gap-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-1 ${ms.status === 'released' ? 'bg-green-500 text-white' : ms.status === 'rejected' ? 'bg-red-200 text-red-700' : 'bg-slate-100 text-primary'}`}>
                          {ms.status === 'released' ? <Check className="w-4 h-4" /> : idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-heading font-bold">{ms.title}</h3>
                            <Badge className={`${config.color} text-xs`}>
                              <StatusIcon className="w-3 h-3 mr-1" /> {config.label}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{ms.description}</p>
                          <p className="text-lg font-heading font-bold text-secondary">₹{ms.amount.toLocaleString('en-IN')}</p>

                          {ms.rejection_reason && (
                            <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Rejected: {ms.rejection_reason}
                            </p>
                          )}

                          {ms.released_at && (
                            <p className="text-xs text-green-600 mt-1">
                              Paid on {new Date(ms.released_at).toLocaleDateString('en-IN')}
                            </p>
                          )}

                          {ms.status !== 'rejected' && <MilestoneSteps status={ms.status} />}

                          {/* Action buttons */}
                          <div className="flex flex-wrap gap-2 mt-3">
                            {isClient && ms.status === 'requested' && (
                              <>
                                <Button size="sm" onClick={() => handleClientApprove(ms.id)} data-testid={`approve-milestone-${ms.id}`}>
                                  <Check className="w-4 h-4 mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => setShowRejectDialog(ms.id)} data-testid={`reject-milestone-${ms.id}`}>
                                  <X className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </>
                            )}
                            {isAdmin && ms.status === 'client_approved' && (
                              <>
                                <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleAdminApprove(ms.id)} data-testid={`admin-approve-${ms.id}`}>
                                  <ShieldCheck className="w-4 h-4 mr-1" /> Release Payment
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => setShowRejectDialog(ms.id)} data-testid={`admin-reject-${ms.id}`}>
                                  <X className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </>
                            )}
                            {isFreelancer && ms.status === 'rejected' && (
                              <Button size="sm" variant="outline" className="text-blue-600 border-blue-300" onClick={() => { setResubmitDialog(ms.id); setResubmitData({ title: ms.title, description: ms.description, amount: String(ms.amount) }); }} data-testid={`resubmit-milestone-${ms.id}`}>
                                <RefreshCw className="w-4 h-4 mr-1" /> Edit & Resubmit
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reject Dialog */}
        <Dialog open={!!showRejectDialog} onOpenChange={() => setShowRejectDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Milestone</DialogTitle>
              <DialogDescription>Provide a reason for rejection</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Reason</Label>
                <Textarea
                  value={rejectReason}
                  onChange={e => setRejectReason(e.target.value)}
                  placeholder="e.g. Work incomplete, quality issues..."
                  data-testid="reject-reason"
                />
              </div>
              <Button onClick={() => handleReject(showRejectDialog)} className="w-full bg-red-600 hover:bg-red-700" data-testid="confirm-reject">
                Confirm Rejection
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Resubmit Dialog */}
        <Dialog open={!!resubmitDialog} onOpenChange={() => setResubmitDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit & Resubmit Milestone</DialogTitle>
              <DialogDescription>Update the milestone details and resubmit for approval.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleResubmit} className="space-y-4" data-testid="resubmit-form">
              <div>
                <Label>Milestone Title</Label>
                <Input
                  value={resubmitData.title}
                  onChange={e => setResubmitData({...resubmitData, title: e.target.value})}
                  required
                  data-testid="resubmit-title"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={resubmitData.description}
                  onChange={e => setResubmitData({...resubmitData, description: e.target.value})}
                  required rows={3}
                  data-testid="resubmit-description"
                />
              </div>
              <div>
                <Label>Amount (₹)</Label>
                <Input
                  type="number" min="1"
                  value={resubmitData.amount}
                  onChange={e => setResubmitData({...resubmitData, amount: e.target.value})}
                  required
                  data-testid="resubmit-amount"
                />
              </div>
              <Button type="submit" className="w-full" disabled={submitting} data-testid="confirm-resubmit">
                {submitting ? 'Resubmitting...' : 'Resubmit Milestone'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Status Messages */}
        {isFreelancer && contract.payment_status === 'pending' && (
          <Card className="mt-6">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">Waiting for client to fund the contract escrow...</p>
            </CardContent>
          </Card>
        )}
        {isFreelancer && contract.payment_status === 'escrowed' && milestones.length === 0 && (
          <Card className="mt-6">
            <CardContent className="pt-6">
              <p className="text-sm text-amber-700 font-medium">Payment is secured in escrow, but no milestones were planned. To use milestone-based payments, they must be set up before the contract is funded.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ContractDetail;
